import type { GlobalRule } from '../schemas/rules.js';

/**
 * What the interviewer receives of the global rules (§8.5, §10.3).
 *
 * §10.3 fixes where this block lands - after the persona and the experience's
 * overlay, before the step's direction - and §8.5 fixes what it says: the
 * rules « par priorité », then the sentence that settles a conflict between
 * two of them.
 *
 * Here rather than in the backend for the reason `persona/render.ts` is here:
 * the screen that edits these rules shows what the interviewer will receive,
 * and a second spelling of the rendering would drift from the one that is
 * actually served. The engine (issue #64) calls this same function with the
 * rules it read at the top of the turn.
 *
 * PURE and TOTAL: it reads the list it is handed and nothing else, and it has
 * no failing input - an empty list is a legal state of §11.3, where the team is
 * free to remove every rule.
 */

/** The sentence that closes the block (§8.5), written once. */
export const RULES_CONFLICT_SENTENCE = 'En cas de conflit, la règle la plus haute prime.';

/**
 * The numbered list of rules, by priority, followed by the conflict sentence.
 *
 * The numbering is the POSITION in the served list and never the stored
 * `priority`: the two agree on a dense list, and the day a read hands back a
 * hole - a row deleted between two reads of a lot that has not landed yet - a
 * block numbered « 1, 2, 4 » would tell a model that a rule is missing.
 *
 * An empty list renders the EMPTY STRING, not the lone conflict sentence: a
 * paragraph about which rule wins, above no rules at all, is noise served on
 * every turn of every session. The caller drops an empty block rather than
 * testing the list itself.
 *
 * Sorted here rather than trusted: the list arrives from a store that already
 * orders it, but this function is also called with a list an editor holds, and
 * a render that depends on its caller having sorted first is a render that is
 * wrong exactly once.
 */
export function rulesDirective(rules: readonly Pick<GlobalRule, 'text' | 'priority'>[]): string {
  if (rules.length === 0) return '';
  const lines = [...rules]
    .sort((one, other) => one.priority - other.priority)
    .map((rule, index) => `${index + 1}. ${rule.text}`);
  return `${lines.join('\n')}\n${RULES_CONFLICT_SENTENCE}`;
}
